import { makeStyles, mergeClasses, tokens } from '@fluentui/react-components';
import { ChevronLeftRegular } from '@fluentui/react-icons';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDiscovery, DiscoveryCategory, DynamicSubItemProps } from '../../../libs/hooks/useDiscovery';

const useClasses = makeStyles({
    container: {
        display: 'flex',
        flexDirection: 'column',
        paddingLeft: '64px',
        paddingRight: '64px',
        gap: '8px',
        alignItems: 'flex-start',
    },
    categoriesContainer: {
        display: 'flex',
        flexDirection: 'row',
        flexWrap: 'wrap',
        gap: '8px',
    },
    category: {
        margin: '4px',
        padding: '8px',
        userSelect: 'none',
        cursor: 'pointer',
        backgroundColor: tokens.colorNeutralBackground1,
        color: tokens.colorNeutralForeground1,
        borderRadius: tokens.borderRadiusXLarge,
        border: `1px solid ${tokens.colorNeutralStroke1}`,
        display: 'inline-block',
        whiteSpace: 'nowrap',
        '&selectors': {
            '&:empty': {
                visibility: 'hidden',
            },
        },
        ':hover': {
            backgroundColor: tokens.colorNeutralBackground4,
            borderTopColor: tokens.colorNeutralForeground3,
            borderRightColor: tokens.colorNeutralForeground3,
            borderBottomColor: tokens.colorNeutralForeground3,
            borderLeftColor: tokens.colorNeutralForeground3,
        },
    },
    subItem: {
        padding: '8px',
        userSelect: 'none',
        cursor: 'pointer',
        color: tokens.colorNeutralForeground1,
        boxSizing: 'border-box',
        border: `1px solid ${tokens.colorNeutralStroke1}`,
        borderRadius: tokens.borderRadiusXLarge,
        ':hover': {
            backgroundColor: tokens.colorNeutralBackground4,
            borderTopColor: tokens.colorNeutralForeground3,
            borderRightColor: tokens.colorNeutralForeground3,
            borderBottomColor: tokens.colorNeutralForeground3,
            borderLeftColor: tokens.colorNeutralForeground3,
        },
    },
    selected: {
        backgroundColor: tokens.colorBrandBackground2,
        color: tokens.colorNeutralForeground1,
    },
    subItemsContainer: {
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
    },
    backButton: {
        display: 'flex',
        alignItems: 'center',
        gap: '4px',
        cursor: 'pointer',
        color: tokens.colorBrandForeground1,
        textDecoration: 'none',
        margin: '0 0 8px 0',
        ':hover': {
            textDecoration: 'underline',
        },
    },
    backButtonText: {
        fontSize: tokens.fontSizeBase300,
    },
    itemHighlight: {
        color: tokens.colorNeutralForeground1,
        fontWeight: tokens.fontWeightRegular,
    },
});

interface CategoryItemProps {
    category: DiscoveryCategory;
    isSelected: boolean;
    onSelect: (category: DiscoveryCategory) => void;
}

const CategoryItem: React.FC<CategoryItemProps> = ({ category, isSelected, onSelect }) => {
    const classes = useClasses();
    return (
        <div
            className={mergeClasses(classes.category, isSelected ? classes.selected : undefined)}
            onClick={() => {
                onSelect(category);
            }}
        >
            {category.name}
        </div>
    );
};

interface SubItemProps {
    item: DynamicSubItemProps;
    onSelect: (item: string) => void;
}

const SubItemComponent: React.FC<SubItemProps> = ({ item, onSelect }) => {
    const classes = useClasses();

    // Handle the new format for reports
    if (typeof item === 'object') {
        return (
            <div
                className={classes.subItem}
                onClick={() => {
                    onSelect(item.prompt);
                }}
            >
                <span title={item.prompt} className={classes.itemHighlight}>
                    {item.userText ?? item.prompt}
                </span>
            </div>
        );
    }

    // Handle existing format for non-reports
    if (typeof item === 'string') {
        return (
            <div
                className={classes.subItem}
                onClick={() => {
                    onSelect(item);
                }}
            >
                {item}
            </div>
        );
    }

    return null;
};

interface DiscoveryQuestionsProps {
    onQuerySubmit: (query: string) => void;
    onQuestionButtonClick: (text: string) => void;
}

export const DiscoveryQuestions: React.FC<DiscoveryQuestionsProps> = ({ onQuestionButtonClick, onQuerySubmit }) => {
    const classes = useClasses();
    const [selectedCategory, setSelectedCategory] = useState<DiscoveryCategory | null>(null);
    const { isEnabled, categories } = useDiscovery();
    const { t } = useTranslation('discovery');

    const handleCategorySelect = (category: DiscoveryCategory) => {
        setSelectedCategory(category);
    };

    const handleSubItemSelect = (item: string) => {
        onQuestionButtonClick(item);
        onQuerySubmit(item);
    };

    const handleBack = () => {
        setSelectedCategory(null);
    };

    if (!isEnabled) {
        return null;
    }

    // Filter out categories that have no items
    const categoriesWithItems = categories.filter((category) => category.items.length > 0);

    if (categoriesWithItems.length === 0) {
        return null;
    }

    return (
        <div className={classes.container}>
            {selectedCategory ? (
                <>
                    <a
                        className={classes.backButton}
                        onClick={(e) => {
                            e.preventDefault();
                            handleBack();
                        }}
                        href="#"
                    >
                        <ChevronLeftRegular />
                        <span className={classes.backButtonText}>{t('back')}</span>
                    </a>
                    <div className={classes.subItemsContainer}>
                        {selectedCategory.items.map((item, index) => (
                            <SubItemComponent key={index} item={item} onSelect={handleSubItemSelect} />
                        ))}
                    </div>
                </>
            ) : (
                <div className={classes.categoriesContainer}>
                    {categoriesWithItems.map((category) => (
                        <CategoryItem
                            key={category.id}
                            category={category}
                            isSelected={false}
                            onSelect={handleCategorySelect}
                        />
                    ))}
                </div>
            )}
        </div>
    );
};
